/**
 * Tab identity — the title and the favicon — driven by the tenant's branding.
 *
 * These are document-level, outside React's tree, so they are set imperatively.
 * The same two values are also applied by the boot script in index.html from
 * the cached branding, before the bundle runs, so a reload does not flash the
 * product name; this module is what keeps them correct afterwards (and when
 * branding or the UI language changes).
 *
 * Keep FAVICON_ID in step with the boot script: it is how the two find the
 * same <link> instead of stacking up duplicates.
 */

export const FAVICON_ID = 'tenant-favicon';

const MIME_BY_EXTENSION = {
  svg: 'image/svg+xml',
  png: 'image/png',
  ico: 'image/x-icon',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
};

// Query strings and data: URLs both break a naive extension match, so only
// take a type when the URL plainly ends in one. Browsers sniff the rest.
function mimeFor(url) {
  const match = /\.([a-z0-9]+)(?:[?#]|$)/i.exec(url || '');
  return match ? MIME_BY_EXTENSION[match[1].toLowerCase()] : undefined;
}

/**
 * Point the tab icon at the tenant's logo. Passing nothing removes the tag,
 * which drops the browser back to its default icon.
 */
export function applyFavicon(url) {
  if (typeof document === 'undefined') return;
  const existing = document.getElementById(FAVICON_ID);

  if (!url) {
    existing?.remove();
    return;
  }

  const link = existing || document.createElement('link');
  if (!existing) {
    link.id = FAVICON_ID;
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  const type = mimeFor(url);
  if (type) link.type = type;
  else link.removeAttribute('type');
  // Compare the attribute, not the property: `link.href` resolves to an
  // absolute URL, so it would never equal a relative value and the guard
  // would never hold.
  if (link.getAttribute('href') !== url) link.href = url;
}

/**
 * Which asset the tab icon comes from. `faviconUrl` is the square icon a
 * tenant uploads for exactly this; the header logo is the fallback, and a wide
 * one letterboxes into an unreadable sliver at 16px — so it is a last resort,
 * not the default.
 */
export function faviconSource(branding) {
  return branding?.faviconUrl || branding?.logoUrl || '';
}
